import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { nomenclatorV2Api, RegionPayload } from '../../services/nomenclatorV2Api'

interface Region {
  id: string
  display_name_en: string
  display_name_ro: string
  sort_order?: number
  is_composite: boolean
}

export const RegionsManager: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [regionToDelete, setRegionToDelete] = useState<string | null>(null)

  const [form, setForm] = useState<RegionPayload>({
    id: '',
    display_name_en: '',
    display_name_ro: '',
    sort_order: undefined,
    is_composite: false
  })

  const loadRegions = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await nomenclatorV2Api.listRegions()
      setRegions(data)
    } catch (err) {
      console.error('Failed to load regions', err)
      setError('Nu am putut încărca regiunile')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRegions()
  }, [])

  const handleSubmit = async () => {
    if (!form.id || !form.display_name_ro) {
      setError('ID și Nume RO sunt obligatorii')
      return
    }

    try {
      if (editingId) {
        await nomenclatorV2Api.updateRegion(editingId, {
          display_name_en: form.display_name_en,
          display_name_ro: form.display_name_ro,
          sort_order: form.sort_order,
          is_composite: form.is_composite
        })
        setSuccess('Regiune actualizată cu succes')
      } else {
        await nomenclatorV2Api.createRegion({
          id: form.id.toUpperCase(),
          display_name_en: form.display_name_en,
          display_name_ro: form.display_name_ro,
          sort_order: form.sort_order,
          is_composite: form.is_composite
        })
        setSuccess('Regiune adăugată cu succes')
      }
      resetForm()
      loadRegions()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la salvare')
    }
  }

  const handleEdit = (region: Region) => {
    setForm({
      id: region.id,
      display_name_en: region.display_name_en,
      display_name_ro: region.display_name_ro,
      sort_order: region.sort_order,
      is_composite: region.is_composite
    })
    setEditingId(region.id)
  }

  const handleDeleteClick = (id: string) => {
    setRegionToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!regionToDelete) return

    try {
      await nomenclatorV2Api.deleteRegion(regionToDelete)
      setSuccess('Regiune ștearsă cu succes')
      loadRegions()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la ștergere')
    } finally {
      setDeleteDialogOpen(false)
      setRegionToDelete(null)
    }
  }

  const resetForm = () => {
    setForm({
      id: '',
      display_name_en: '',
      display_name_ro: '',
      sort_order: undefined,
      is_composite: false
    })
    setEditingId(null)
  }

  if (loading && regions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  // Compact input styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem', height: 32 },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.78rem' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        message={success}
      />

      {/* Form - Compact */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AddIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {editingId ? 'Editează Regiune' : 'Adaugă Regiune Nouă'}
            </Typography>
          </Box>

          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            <TextField
              label="Cod (EN)"
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value.toUpperCase() })}
              disabled={!!editingId}
              required
              size="small"
              sx={{ width: 120, ...inputSx }}
            />
            <TextField
              label="Nume RO"
              value={form.display_name_ro}
              onChange={(e) => setForm({ ...form, display_name_ro: e.target.value })}
              required
              size="small"
              sx={{ flex: 1, minWidth: 150, ...inputSx }}
            />
            <TextField
              label="Nume EN"
              value={form.display_name_en}
              onChange={(e) => setForm({ ...form, display_name_en: e.target.value })}
              size="small"
              sx={{ flex: 1, minWidth: 150, ...inputSx }}
            />
            <TextField
              label="Ordine"
              type="number"
              value={form.sort_order ?? ''}
              onChange={(e) =>
                setForm({ ...form, sort_order: e.target.value ? Number(e.target.value) : undefined })
              }
              size="small"
              sx={{ width: 80, ...inputSx }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_composite}
                  onChange={(e) => setForm({ ...form, is_composite: e.target.checked })}
                  size="small"
                  sx={{ p: 0.25 }}
                />
              }
              label={<Typography sx={{ fontSize: '0.75rem' }}>Compusă</Typography>}
              sx={{ ml: 0, mr: 1 }}
            />
            <Button 
              variant="contained" 
              onClick={handleSubmit} 
              disabled={!form.id || !form.display_name_ro}
              size="small"
              sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
            >
              {editingId ? 'Salvează' : 'Adaugă'}
            </Button>
            {editingId && (
              <Button 
                variant="outlined" 
                onClick={resetForm}
                size="small"
                sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
              >
                Anulează
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Table - Compact */}
      <Card sx={{
        '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' },
        '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 },
        '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
      }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
            Regiuni ({regions.length})
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.900',
                '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 }
              }}>
                <TableCell>Cod</TableCell>
                <TableCell>Nume RO</TableCell>
                <TableCell>Nume EN</TableCell>
                <TableCell align="right">Ordine</TableCell>
                <TableCell>Compusă</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {regions.map((region) => (
                <TableRow key={region.id} hover>
                  <TableCell><strong>{region.id}</strong></TableCell>
                  <TableCell>{region.display_name_ro}</TableCell>
                  <TableCell>{region.display_name_en}</TableCell>
                  <TableCell align="right">{region.sort_order ?? '-'}</TableCell>
                  <TableCell>{region.is_composite ? 'Da' : 'Nu'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(region)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(region.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Dialog - Compact */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
          Confirmare ștergere
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.85rem' }}>
            Sigur vrei să ștergi regiunea <strong>{regionToDelete}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>Anulează</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>
            Șterge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

